import { getActiveTraceHttpRequestHeaders } from '@atlaskit/react-ufo/experience-trace-id-context';

import { RestClient } from '@townsquare/rest-client';

export type InviteToProjectRequest = {
	email: string;
	projectRoleIds: number[];
	atlOrigin: string;
	continueUrl: string;
};

export type InviteStatus =
	| 'INVITED'
	| 'INVITED_PENDING_APPROVAL'
	| 'PENDING_INVITE_EXISTS'
	| 'USER_EXISTS'
	| 'LICENSE_LIMIT_REACHED'
	| 'NOT_INVITED';

export type InviteToProjectResponse = {
	inviteeAaId: string;
	inviteStatus: InviteStatus;
};

class JiraRestClient extends RestClient {
	/**
	 * Invites a user to a Jira project by email
	 * @param cloudId - The cloud ID of the Jira instance
	 * @param projectId - The ID of the project to invite the user to
	 * @param payload - The invite request payload containing email, roles, origin, and continue URL
	 * @param recaptchaToken - Optional recaptcha token for bot prevention
	 * @returns A promise that resolves to the invite response
	 */
	async requestSpaceAccessForUser({
		cloudId,
		projectId,
		payload,
		recaptchaToken,
	}: {
		cloudId: string;
		projectId: string;
		payload: InviteToProjectRequest;
		recaptchaToken?: string;
	}): Promise<InviteToProjectResponse> {
		const response = await this.fetchResource(
			`/gateway/api/ex/jira/${cloudId}/rest/internal/latest/invite-user/request-access/project/${projectId}/`,
			'POST',
			{
				body: JSON.stringify(payload),
				headers: {
					'content-type': 'application/json',
					...(recaptchaToken ? { 'G-Recaptcha-Token': recaptchaToken } : {}),
					...getActiveTraceHttpRequestHeaders(),
				},
			},
		);
		return response.json();
	}
}

// Create and export singleton instance
// Use empty baseUrl so paths are relative to current domain
const jiraRestClient: JiraRestClient = new JiraRestClient({ baseUrl: '' });

export { jiraRestClient };
